import * as Haptics from 'expo-haptics';
import React from 'react';
import { Pressable } from 'react-native';

import { HStack, Text } from '@squire/ui';

import {
  MoonFilledIcon,
  MoonIcon,
  MorningFilledIcon,
  MorningIcon,
  SunFilledIcon,
  SunIcon,
} from '@/components/_icons';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { pressedState } from '@/utils/a11y';
import { formatTimeSlot } from '@/utils/dateUtils';

import { styles } from './styles';
import type { TimePeriod, TimeSlotButtonProps } from './types';

const getPeriodIcon = (period: TimePeriod, isSelected: boolean) => {
  const iconColor = isSelected ? 'iconOnSelected' : 'iconPrimary';
  switch (period) {
    case 'morning':
      return isSelected ? (
        <MorningFilledIcon color={iconColor} />
      ) : (
        <MorningIcon color={iconColor} />
      );
    case 'afternoon':
      return isSelected ? (
        <SunFilledIcon color={iconColor} />
      ) : (
        <SunIcon color={iconColor} />
      );
    case 'evening':
      return isSelected ? (
        <MoonFilledIcon color={iconColor} />
      ) : (
        <MoonIcon color={iconColor} />
      );
  }
};

export function TimeSlotButton(props: TimeSlotButtonProps) {
  const { time, period, isSelected, isAvailable, onPress, testID, style } =
    props;

  const { t } = useScreenTranslations('TimeSlotButton');

  styles.useVariants({
    state: isSelected ? 'selected' : isAvailable ? undefined : 'disabled',
  });

  const handlePress = () => {
    if (isAvailable) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      onPress();
    }
  };

  const timeString = formatTimeSlot(time);
  const periodLabel = period.charAt(0).toUpperCase() + period.slice(1);

  return (
    <Pressable
      style={[styles.button, style]}
      onPress={handlePress}
      disabled={!isAvailable}
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={`${timeString}, ${periodLabel}`}
      {...pressedState(isSelected)}
      aria-disabled={!isAvailable || undefined}
      accessibilityHint={
        isAvailable
          ? t('accessibility.tapToSelect')
          : t('accessibility.notAvailable')
      }
    >
      <HStack space={8} centerVertical>
        {getPeriodIcon(period, isSelected)}
        <Text
          variant="mStrong"
          color={isSelected ? 'textOnSelected' : 'textPrimary'}
        >
          {timeString}
        </Text>
      </HStack>
    </Pressable>
  );
}
